<template>
  <div v-if="visible" class="lightbox" @click="hide" @keydown.27="hide">
      <div class="lightbox__close" @click="hide">
        <slot name="icon-close">
          &times;
        </slot>
      </div>
      <div class="lightbox__element" @click.stop="">
        <div
          :class="['lightbox__arrow lightbox__arrow--left', { 'lightbox__arrow--invisible': !hasPrevious }]"
          @click.stop.prevent="previous"
        >
          <slot name="icon-previous">
            <svg width="40" height="40" viewBox="0 0 24 24" class="lightbox__arrow-icon" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 16.09l-4.58-4.59 4.58-4.59L14 5.5l-6 6 6 6z" />
              <path d="M0-.5h24v24H0z" fill="none" />
            </svg>
          </slot>
        </div>
        <div class="lightbox__content" @click.stop="">
          <slot name="loader">
            <BaseLoader v-if="!loaded"/>
          </slot>

          <slot v-if="displayContent" name="content" :url="items[index]">
            <component :is="container" v-bind="containerProps" :src="extractSrc(items[index])" class="lightbox__component" @load="onLoad" />
          </slot>
        </div>
        <div
          :class="['lightbox__arrow lightbox__arrow--right', { 'lightbox__arrow--invisible': !hasNext }]"
          @click.stop.prevent="next"
        >
          <slot name="icon-next">
            <svg width="40" height="40" viewBox="0 0 24 24" class="lightbox__arrow-icon" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z" />
              <path d="M0-.25h24v24H0z" fill="none" />
            </svg>
          </slot>
        </div>
      </div>
    </div>
</template>

<script>
  import Vue from 'vue'
  import isObject from 'lodash/isObject'
  import isString from 'lodash/isString'

  import BaseLoader from '../components/BaseLoader.vue'
  import BaseVideo from '../components/BaseVideo.vue'
  import BaseImage from '../components/BaseImage.vue'
  export { BaseLoader }

  export default {
    components: {
      BaseLoader
    },
    props: {
      value: {
        type: Boolean,
        default: false,
      }
    },
    data() {
      return {
        visible: this.value,
        openAtIndex: 0,
        index: 0,
        displayContent: true,
        items: [],
        containerProps: {},
        loaded: false
      }
    },
    computed: {
      hasNext() {
        return (this.index + 1 < this.items.length)
      },
      hasPrevious() {
        return (this.index - 1 >= 0)
      },
      container() {
        let item = this.items[this.index];

        if(isObject(item)) item = item.src;

        if(/(youtu|mover)/.test(item)) return BaseVideo;
        if(/.*\.(jpg|png|svg)/.test(item)) return BaseImage;
        return BaseImage
      }
    },
    watch: {
      value(newValue) {
        this.visible = newValue
      },
      visible(newVisibility) {
        this.$emit('input', newVisibility)
      },
    },
    mounted() {
      this.$root.$on('lightboxOpen', this.show);
      this.$router && this.$router.beforeResolve((to, from, next) => {
        if(this.visible) {
          this.hide();
          next(false);
        } else {
          next();
        }
      });

    },
    destroyed() {
      this.$root.$off('lightboxOpen', this.show);
    },
    methods: {
      onLoad() {
        this.loaded = true;
      },
      show(items, index, containerProps = {}) {
        this.containerProps = containerProps;
        this.visible = true;
        this.items = items;
        this.index = this.openAtIndex = index;
        // this.requestFullscreen();

        // window.addEventListener("popstate", this.hide, false);

        window.addEventListener('keydown', this.eventListener);
      },
      hide(ev) {
        ev && ev.preventDefault();
        this.visible = false;
        this.index = this.openAtIndex;
        // this.exitFullscreen();
        window.removeEventListener('keydown', this.eventListener);
        return false;
      },
      requestFullscreen() {
        if(typeof this.$el.requestFullscreen === 'function') this.$el.requestFullscreen();
      },
      exitFullscreen() {
        if(document && typeof document.exitFullscreen === 'function') document.exitFullscreen();
      },
      previous() {
        if (this.hasPrevious) {
          this.index -= 1;
          this.tick()
        }
      },
      next() {
        if (this.hasNext) {
          this.index += 1;
          this.tick()
        }
      },
      tick() {
        if (!this.$slots.loader) {
          return
        }

        this.displayContent = false;

        Vue.nextTick(() => {
          this.displayContent = true
        })
      },
      eventListener(e) {
        if (this.visible) {
          switch (e.key) {
            case 'ArrowRight':
              return this.next();
            case 'ArrowLeft':
              return this.previous();
            case 'ArrowDown':
            case 'ArrowUp':
            case ' ':
              return e.preventDefault();
            case 'Escape':
            case 'Backspace':
              return this.hide(e)
          }
        }
      },
      whatType(item) {
        if(isObject(item)) return this.whatType(item.src);
        if(/.*\.(jpg|png|svg)/.test(item)) return 'img';
        if(/.(youtu|mover)/.test(item)) return 'video';
      },
      extractSrc(item) {
        if(isObject(item)) return item.src;
        if(isString(item)) return item;
        return null;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .lightbox {
    position: fixed;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, .8);
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
    cursor: pointer;
  }

  .lightbox__close {
    position: fixed;
    z-index: 2;
    right: 0;
    top: 0;
    font-size: 2.5rem;
    line-height: 5rem;
    text-align: center;
    cursor: pointer;
    color: #fff;
    width: 5rem;
    height: 5rem;
    box-sizing: border-box;
  }

  .lightbox__arrow--invisible {
    visibility: hidden;
  }

  .lightbox__element {
    display: flex;
    width: 100%;
    height: fit-content;
    max-height: 95vh;
    cursor: default;
  }
  .lightbox__component.asm-video {
    min-width: 80vw;
  }

  .lightbox__arrow {
    width: 5rem;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1 1 auto;
    z-index: 1;
  }

  .lightbox__arrow-icon {
    fill: #fff;
    pointer-events: none;
  }

  .lightbox__content {
    flex: 0 1 auto;
  }

  .lightbox__content img {
    max-width: 100%;
    height: auto !important;
  }

  @media screen and (max-width: 720px) {
    .lightbox__element,
    .lightbox__content{
      position: relative;
      max-height: 100vh;
    }
    .lightbox__component.asm-video {
      position: relative;
      width: 100vw;
      z-index: 300;
    }

    .lightbox__arrow {
      position: absolute;
      width: 5rem;
      height: 100%;
    }

    .lightbox__arrow--right {
      right: 0;
      background: linear-gradient(to right, transparent, rgba(0, 0, 0, .3));
    }

    .lightbox__arrow--left {
      left: 0;
      background: linear-gradient(to left, transparent, rgba(0, 0, 0, .3));
    }
  }
</style>
